import { Component, OnInit } from '@angular/core';
import { Dashboard, DashboardRoute, CreateUpdateDashboardModel } from '../domain/models/dashboard';
import { RouteImport } from '../domain/models/route-import';
import { RouteImportType } from '../domain/enums/routeImportType';
import { DashboardOverviewService } from './services/dashboard-overview.service';
import { DashboardJobStatus } from '../domain/enums/DashboardJobStatus';
import * as _ from 'lodash';

@Component({
  selector: 'app-dashboard-overview',
  templateUrl: './dashboard-overview.component.html',
  styleUrls: ['./dashboard-overview.component.scss']
})
export class DashboardOverviewComponent implements OnInit {
  public dashboards: Dashboard[];
  public selectedDashboard: Dashboard;
  public addedRoutes: RouteImport[];
  public availableRoutes: RouteImport[];
  public readonly routeImportType: RouteImportType;

  private expandedDashboardIds: string[];
  private dashboardsLoading: boolean;
  private createDashboardVisible: boolean;
  private routeImportVisible: boolean;
  private deleteDashboardVisible: boolean;
  private updateDashboardVisible: boolean;

  constructor(
    private dashboardOverviewService: DashboardOverviewService) {
    this.routeImportType = RouteImportType.Dashboard;
  }

  ngOnInit() {
    this.dashboards = [];
    this.expandedDashboardIds = [];
    this.getDashboards();
  }

  private getDashboards(dashboardToCompleteName?: string): void {
    this.dashboardsLoading = true;
    this.dashboardOverviewService.getAllDashboards()
      .subscribe((dashboards: Dashboard[]) => {
        this.dashboards = dashboards;
        this.dashboardsLoading = false;
        if (dashboardToCompleteName) {
          let dbs = _.filter(this.dashboards, (dash: Dashboard) => {
            return dash.name === dashboardToCompleteName;
          });
          if (dbs && dbs.length > 0) {
            this.showRouteImport(_.last(dbs));
          }
        }
      }, (error: any) => {
        this.dashboardsLoading = false;
      });

  }

  dashboardsAreLoading(): boolean {
    return this.dashboardsLoading;
  }

  hasDashboards(): boolean {
    return this.dashboards && this.dashboards.length > 0;
  }

  dashboardHasRoutes(dashboard: Dashboard): boolean {
    return dashboard.jobs && dashboard.jobs.length > 0;
  }

  getDashboardOverviewVisible(): boolean {
    return !this.dashboardsLoading && !this.createDashboardVisible && !this.routeImportVisible && !this.deleteDashboardVisible && !this.updateDashboardVisible;
  }

  getCreateDashboardVisible(): boolean {
    return this.createDashboardVisible;
  }

  getUpdateDashboardVisible(): boolean {
    return this.updateDashboardVisible;
  }

  getRouteImportVisible(): boolean {
    return this.routeImportVisible;
  }

  getDeleteDashboardVisible(): boolean {
    return this.deleteDashboardVisible;
  }

  showCreateDashboard(): void {
    this.createDashboardVisible = true;
  }

  closeCreateDashboard(): void {
    this.selectedDashboard = null;
    this.createDashboardVisible = false;
  }

  showUpdateDashboard(dashboard: Dashboard): void {
    this.selectedDashboard = dashboard;
    this.updateDashboardVisible = true;
  }

  closeUpdateDashboard(): void {
    this.selectedDashboard = null;
    this.updateDashboardVisible = false;
  }

  showRouteImport(dashboard: Dashboard): void {
    this.selectedDashboard = dashboard;
    this.addedRoutes = this.orderRoutesAscending(
      this.selectedDashboard.jobs.map((job: DashboardRoute) => new RouteImport(job))
    );

    this.dashboardOverviewService.getActiveRoutes()
      .subscribe((result: Array<RouteImport[]>) => {
        let allRoutes = [].concat.apply([], result);

        this.availableRoutes = [];
        _.each(allRoutes, (item: RouteImport) => {
          const routeExists: boolean = _.some(this.addedRoutes, (route: RouteImport) => {
            return route.id === item.id;
          });

          if (!routeExists) {
            this.availableRoutes.push(item);
          }
        });

        this.availableRoutes = this.orderRoutesAscending(this.availableRoutes);
      });

    this.routeImportVisible = true;
  }

  closeRouteImport(): void {
    this.routeImportVisible = false;
    this.selectedDashboard = null;
    this.addedRoutes = [];
    this.availableRoutes = [];
  }

  showDeleteDashboard(dashboard: Dashboard): void {
    this.selectedDashboard = dashboard;
    this.deleteDashboardVisible = true;
  }

  closeDeleteDashboard(): void {
    this.selectedDashboard = null;
    this.deleteDashboardVisible = false;
  }

  private orderRoutesAscending(list: RouteImport[]): RouteImport[] {
    return _.orderBy(list, ['name'], ['asc']);
  }

  createDashboard(dashboard: CreateUpdateDashboardModel): void {
    this.dashboardOverviewService.createDashboard(dashboard)
      .finally(() => {
        this.closeCreateDashboard();
        this.getDashboards(dashboard.name);
      })
      .subscribe();
  }

  updateDashboard(dashboard: CreateUpdateDashboardModel): void {
    this.dashboardOverviewService.updateDashboard(dashboard)
      .finally(() => {
        this.closeUpdateDashboard();
        this.getDashboards();
      })
      .subscribe();
  }

  onSaveRoutes(routes: RouteImport[]): void {
    this.selectedDashboard.jobs = [];

    _.each(routes, (route: RouteImport) => {

      if (!route.toRemove) {
        this.selectedDashboard.jobs.push(route);
      }
    });
    let updated = new CreateUpdateDashboardModel();
    updated.id = this.selectedDashboard.id;
    updated.name = this.selectedDashboard.name;
    updated.url = this.selectedDashboard.url;
    updated.alernatingInterval = this.selectedDashboard.alernatingInterval;
    updated.fitBoundsToHighlightedRoute = this.selectedDashboard.fitBoundsToHighlightedRoute;
    updated.jobs = this.selectedDashboard.jobs;
    updated.language = this.selectedDashboard.language;

    this.dashboardOverviewService.updateDashboard(updated)
      .finally(() => {
        this.closeRouteImport();
        this.getDashboards();
      })
      .subscribe();
  }

  onDeleteDashboard(dashboardId: string): void {
    this.dashboardOverviewService.deleteDashboard(dashboardId)
      .finally(() => {
        this.closeDeleteDashboard();
        this.getDashboards();
      })
      .subscribe();
  }

  toggleExpand(alertId: string) {
    let index = _.indexOf(this.expandedDashboardIds, alertId);
    if (index >= 0) {
      this.expandedDashboardIds.splice(index, 1);
    } else {
      this.expandedDashboardIds.push(alertId);
    }
  }

  expanded(alertId: string): boolean {
    return _.includes(this.expandedDashboardIds, alertId);
  }

  openDashboard(url: string): void {
    window.open(url);
  }

  copyToClipboard(url: string): void {
    let textArea = <HTMLInputElement>document.createElement('textArea');
    textArea.value = url;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
    } catch (error) { }

    document.body.removeChild(textArea);
  }

  containsInactiveJobs(dashboard: Dashboard): boolean {
    let result = _.filter(dashboard.jobs, (job: DashboardRoute) => {
      return job.status !== DashboardJobStatus.Running && job.status !== DashboardJobStatus.Submitted;
    });
    return result.length > 0;
  }

  jobIsFinished(job: DashboardRoute): boolean {
    return job.status !== DashboardJobStatus.Running && job.status !== DashboardJobStatus.Submitted;
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/dashboard-overview/dashboard-overview.component.ts